import React, { useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { DailyFeedMix, nutritionApi } from '../api/nutrition';
import { format } from 'date-fns';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface BatchOperationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMixes: DailyFeedMix[];
  onOperationComplete: () => void;
}

type BatchOperation = 'approve' | 'reject' | 'delete' | 'export';

export default function BatchOperationsModal({
  isOpen,
  onClose,
  selectedMixes,
  onOperationComplete
}: BatchOperationsModalProps) {
  const [selectedOperation, setSelectedOperation] = useState<BatchOperation>('approve');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [operationResults, setOperationResults] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const operations = [
    {
      id: 'approve' as const,
      name: 'Approve Selected',
      description: 'Mark selected feed mixes as approved',
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'reject' as const,
      name: 'Reject Selected',
      description: 'Mark selected feed mixes as rejected',
      icon: XCircleIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    {
      id: 'delete' as const,
      name: 'Delete Selected',
      description: 'Permanently delete selected feed mixes (only unapproved)',
      icon: ExclamationTriangleIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    {
      id: 'export' as const,
      name: 'Export Selected',
      description: 'Export selected feed mixes to CSV/JSON',
      icon: ClockIcon,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-blue-200'
    }
  ];

  const selectedOperationData = operations.find(op => op.id === selectedOperation);

  const canDelete = selectedMixes.every(mix => !mix.metadata?.approved);
  const pendingCount = selectedMixes.filter(mix => !mix.metadata?.approved).length;
  const approvedCount = selectedMixes.filter(mix => mix.metadata?.approved).length;

  const handleExecuteOperation = async () => {
    if (selectedMixes.length === 0) return;

    setIsProcessing(true);
    setProcessingProgress(0);
    setOperationResults(null);

    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    try {
      for (let i = 0; i < selectedMixes.length; i++) {
        const mix = selectedMixes[i];
        setProcessingProgress(((i + 1) / selectedMixes.length) * 100);

        try {
          switch (selectedOperation) {
            case 'approve':
              if (!mix.metadata?.approved) {
                await nutritionApi.approveDailyFeedMix(mix.id, true, 'Current User', approvalNotes);
                results.success++;
              } else {
                results.success++; // Already approved
              }
              break;

            case 'reject':
              if (!mix.metadata?.approved) {
                await nutritionApi.approveDailyFeedMix(mix.id, false, 'Current User', rejectionReason);
                results.success++;
              } else {
                results.errors.push(`Mix ${mix.id} is already approved and cannot be rejected`);
                results.failed++;
              }
              break;

            case 'delete':
              if (!mix.metadata?.approved) {
                await nutritionApi.deleteDailyFeedMix(mix.id);
                results.success++;
              } else {
                results.errors.push(`Mix ${mix.id} is approved and cannot be deleted`);
                results.failed++;
              }
              break;

            case 'export':
              // Export functionality would be implemented here
              results.success++;
              break;

            default:
              results.errors.push(`Unknown operation: ${selectedOperation}`);
              results.failed++;
          }
        } catch (error: any) {
          results.errors.push(`Failed to process mix ${mix.id}: ${error.message}`);
          results.failed++;
        }
      }

      setOperationResults(results);
      
      if (results.success > 0) {
        onOperationComplete();
      }
    } catch (error) {
      console.error('Batch operation failed:', error);
    } finally {
      setIsProcessing(false);
      setProcessingProgress(100);
    }
  };

  const handleExportToCsv = () => {
    const headers = [
      'Date',
      'Location',
      'Total Quantity (kg)',
      'Protein %',
      'Fat %',
      'Fiber %',
      'Quality Score',
      'Total Cost',
      'Approved',
      'Operator',
      'Notes'
    ];

    const csvData = selectedMixes.map(mix => [
      format(new Date(mix.feedDate), 'yyyy-MM-dd'),
      mix.location || '',
      mix.totalQuantity.toString(),
      mix.nutritionalSummary.proteinPercentage.toString(),
      mix.nutritionalSummary.fatPercentage.toString(),
      mix.nutritionalSummary.fiberPercentage.toString(),
      mix.nutritionalSummary.qualityScore.toString(),
      (mix.totalCost || 0).toString(),
      mix.metadata?.approved ? 'Yes' : 'No',
      mix.operator || '',
      mix.notes || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feed-mixes-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={`Batch Operations (${selectedMixes.length} selected)`}
        onClose={onClose}
      />
      <BrandModalBody>
                {/* Summary */}
                <div className="mb-6 p-4 bg-slate-50/80 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-900 mb-2">Selection Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Total selected:</span>
                      <span className="ml-2 font-medium">{selectedMixes.length}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Pending approval:</span>
                      <span className="ml-2 font-medium text-yellow-600">{pendingCount}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Already approved:</span>
                      <span className="ml-2 font-medium text-green-600">{approvedCount}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Date range:</span>
                      <span className="ml-2 font-medium">
                        {selectedMixes.length > 0 ? 
                          `${format(new Date(Math.min(...selectedMixes.map(m => new Date(m.feedDate).getTime()))), 'MMM dd')} - 
                           ${format(new Date(Math.max(...selectedMixes.map(m => new Date(m.feedDate).getTime()))), 'MMM dd')}` 
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Operation Selection */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-slate-900 mb-3">Select Operation</h4>
                  <div className="space-y-2">
                    {operations.map((operation) => {
                      const isDisabled = operation.id === 'delete' && !canDelete;
                      return (
                        <div
                          key={operation.id}
                          className={`relative flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedOperation === operation.id
                              ? `${operation.borderColor} ${operation.bgColor}`
                              : 'border-gray-200 hover:bg-slate-50/80'
                          } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => !isDisabled && setSelectedOperation(operation.id)}
                        >
                          <input
                            type="radio"
                            name="operation"
                            value={operation.id}
                            checked={selectedOperation === operation.id}
                            disabled={isDisabled}
                            onChange={() => !isDisabled && setSelectedOperation(operation.id)}
                            className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center">
                              <operation.icon className={`h-5 w-5 ${operation.color} mr-2`} />
                              <label className="text-sm font-medium text-slate-900 cursor-pointer">
                                {operation.name}
                              </label>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{operation.description}</p>
                            {isDisabled && (
                              <p className="text-xs text-red-500 mt-1">
                                Cannot delete approved feed mixes
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Operation-specific inputs */}
                {selectedOperation === 'approve' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Approval Notes (optional)
                    </label>
                    <textarea
                      value={approvalNotes}
                      onChange={(e) => setApprovalNotes(e.target.value)}
                      rows={3}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Add notes about this batch approval..."
                    />
                  </div>
                )}

                {selectedOperation === 'reject' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Rejection Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Explain why these feed mixes are being rejected..."
                      required
                    />
                  </div>
                )}

                {/* Selected Items Details */}
                <div className="mb-6">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center text-sm font-medium text-slate-700 hover:text-slate-900"
                  >
                    {showDetails ? (
                      <ChevronUpIcon className="h-4 w-4 mr-1" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 mr-1" />
                    )}
                    Show Selected Items ({selectedMixes.length})
                  </button>
                  
                  {showDetails && (
                    <div className="mt-3 max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                      <div className="divide-y divide-gray-200">
                        {selectedMixes.map((mix) => (
                          <div key={mix.id} className="p-3 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-slate-900">
                                {format(new Date(mix.feedDate), 'PPP')}
                              </div>
                              <div className="text-xs text-slate-500">
                                {mix.location || 'No location'} • Quality: {mix.nutritionalSummary.qualityScore}/100
                              </div>
                            </div>
                            <div className="flex items-center">
                              {mix.metadata?.approved ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  Approved
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                  <ClockIcon className="h-3 w-3 mr-1" />
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Processing Progress */}
                {isProcessing && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Processing...</span>
                      <span className="text-sm text-slate-500">{Math.round(processingProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${processingProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Operation Results */}
                {operationResults && (
                  <div className="mb-6 p-4 bg-slate-50/80 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-900 mb-2">Operation Results</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-green-600">Successful:</span>
                        <span className="font-medium">{operationResults.success}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-600">Failed:</span>
                        <span className="font-medium">{operationResults.failed}</span>
                      </div>
                      {operationResults.errors.length > 0 && (
                        <div className="mt-3">
                          <span className="text-red-600 font-medium">Errors:</span>
                          <ul className="mt-1 text-xs text-red-600 space-y-1">
                            {operationResults.errors.map((error, index) => (
                              <li key={index}>• {error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
      </BrandModalBody>
      <BrandModalFooter>
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              {operationResults ? 'Close' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={selectedOperation === 'export' ? handleExportToCsv : handleExecuteOperation}
              disabled={
                isProcessing || 
                selectedMixes.length === 0 || 
                (selectedOperation === 'reject' && !rejectionReason.trim()) ||
                (selectedOperation === 'delete' && !canDelete)
              }
              className={`btn text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedOperationData?.id === 'delete' || selectedOperationData?.id === 'reject'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'btn-primary'
              }`}
            >
              {isProcessing ? 'Processing...' : `${selectedOperationData?.name || 'Execute'}`}
            </button>
      </BrandModalFooter>
    </BrandModal>
  );
} 